// Shared Konva paint decorations reused by slice renderers (textbox, rectangle,
// …). Keeps the dash-pattern formula and drop-shadow attrs in ONE place so the
// border-style / box-shadow options behave identically across object types.

import type Konva from 'konva';

export type StrokeStyle = 'solid' | 'dashed' | 'dotted';

/**
 * Konva `dash` array for a stroke style, scaled to the stroke width so the
 * pattern stays proportional. `solid` (or undefined) → no dashes.
 */
export function konvaDashPattern(style: StrokeStyle | undefined, strokeWidth: number): number[] {
  const s = style ?? 'solid';
  if (s === 'solid') return [];
  const w = Math.max(1, strokeWidth);
  if (s === 'dashed') return [w * 4, w * 3];
  return [w, w * 2]; // dotted
}

/**
 * Konva drop-shadow attrs for the on/off box-shadow toggle. When off, the
 * shadow is explicitly disabled AND its blur/opacity zeroed so a node that
 * previously had a shadow is fully reset.
 */
export function konvaDropShadowAttrs(on: boolean): Konva.RectConfig {
  if (on) {
    return {
      shadowColor: '#000000',
      shadowBlur: 18,
      shadowOffset: { x: 3, y: 5 },
      shadowOpacity: 0.22,
      shadowEnabled: true,
    };
  }
  return { shadowEnabled: false, shadowBlur: 0, shadowOpacity: 0 };
}

/**
 * Konva folds slant and weight into its single `fontStyle` attr — it builds the
 * canvas font as `${fontStyle} ${fontVariant} ${fontSize}px ${fontFamily}`.
 * The CSS font shorthand accepts style and weight in either order, so emitting
 * `"italic 700"` yields a valid `"italic 700 normal 14px …"`.
 *
 * 500 collapses to `normal` and 700 to `bold` because those keywords are what
 * canvas resolves most reliably for fonts that ship only two weights.
 */
export function konvaFontStyle(fontWeight: number | undefined, italic: boolean): string {
  const weight =
    fontWeight === undefined || fontWeight === 500 ? 'normal'
    : fontWeight === 700 ? 'bold'
    : String(fontWeight);
  if (!italic) return weight;
  return weight === 'normal' ? 'italic' : `italic ${weight}`;
}
